import type React from 'react';
import { type PropsWithChildren, useMemo, useState } from 'react';

import { Grid } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import AlertIcon from 'assets/icons/icn _ 20px _ alert-filled.svg?react';
import ErrorIcon from 'assets/icons/icn _ 20px _ close-filled.svg?react';
import SuccessIcon from 'assets/icons/notification_success_green.svg?react';
import { levelExtensionAtom } from 'atoms/tournament';
import TMXAccordion from 'components/accordion/TMXAccordion';
import { useStyles } from 'components/dialogs/addToEventDialog/styles';
import ParticipantsNamesGrid from 'components/participants/ParticipantsNamesGrid';
import { BodyRegular, FormLabelTextLarge } from 'components/typography/Typography';
import { type PersonEventEligibility } from 'typedefs/event';

import EligibilityEmptyState from './EligibilityEmptyState';
import EligibilityRulesTable from './EligibilityRulesTable';
import { getSortedParticipantsEligibility } from './utils';

interface EligibilityContainerProps {
  participantsEventEligibility: PersonEventEligibility[];
  isTeam?: boolean;
}

export interface ParticipantsEligibilityDetail {
  status: string;
  participantsEligibility: PersonEventEligibility[];
  summary: string;
  summaryDetail: string;
  icon: React.ReactNode;
}

export interface DisplayParticipantsEligibility {
  eligible: boolean;
  partEligible: boolean;
  ineligible: boolean;
}

const EligibilityContainer: React.FC<PropsWithChildren<EligibilityContainerProps>> = ({
  participantsEventEligibility,
  isTeam,
}) => {
  const { t } = useTranslation();
  const { level } = useAtomValue(levelExtensionAtom);
  const { classes } = useStyles();
  const [displayHide, setDisplayHide] = useState<DisplayParticipantsEligibility>({
    eligible: true,
    partEligible: true,
    ineligible: true,
  });
  const isUnsanctionedTournament = level?.name === 'Unsanctioned';
  const { eligibleParticipants, partEligibleParticipants, ineligibleParticipants } = useMemo(
    () => getSortedParticipantsEligibility(participantsEventEligibility, isUnsanctionedTournament),
    [participantsEventEligibility, isUnsanctionedTournament],
  );

  const participantsEligibilityDetails = useMemo((): ParticipantsEligibilityDetail[] => {
    return [
      {
        status: 'eligible',
        participantsEligibility: eligibleParticipants,
        summary: t(isTeam ? 'numberEligibleTeams' : 'numberEligiblePlayers', {
          number: eligibleParticipants?.length,
        }),
        summaryDetail: 'will be added',
        icon: <SuccessIcon />,
      },
      {
        status: 'partEligible',
        participantsEligibility: partEligibleParticipants,
        summary: t(isTeam ? 'numberPartEligibleTeams' : 'numberPartEligiblePlayers', {
          number: partEligibleParticipants?.length,
        }),
        summaryDetail: 'may be added',
        icon: <AlertIcon />,
      },
      {
        status: 'ineligible',
        participantsEligibility: ineligibleParticipants,
        summary: t(isTeam ? 'numberIneligibleTeams' : 'numberIneligiblePlayers', {
          number: ineligibleParticipants?.length,
        }),
        summaryDetail: 'cannot be added',
        icon: <ErrorIcon />,
      },
    ];
  }, [eligibleParticipants, ineligibleParticipants, isTeam, partEligibleParticipants, t]);

  const eligibilitySections = useMemo(
    () =>
      participantsEligibilityDetails?.map((eligibilityDetails: ParticipantsEligibilityDetail) => {
        if (eligibilityDetails?.participantsEligibility?.length) {
          const { status, summary, summaryDetail, icon, participantsEligibility } = eligibilityDetails;
          return (
            <Grid key={eligibilityDetails?.status} item>
              <TMXAccordion
                defaultExpanded
                unmountOnClose
                accordionSummaryChildren={
                  <Grid container alignItems="center" direction="row" gap={1}>
                    <Grid item>{icon}</Grid>
                    <Grid item display="flex">
                      <BodyRegular>{summary ? summary : t('pyr')}</BodyRegular>
                      {!!summaryDetail && <BodyRegular sx={{ marginLeft: 1 }}>{`- ${summaryDetail}`}</BodyRegular>}
                    </Grid>
                    <Grid item className={classes.accordionSummaryChildren}>
                      <BodyRegular>{t(displayHide[status] ? 'hide' : 'show')}</BodyRegular>
                    </Grid>
                  </Grid>
                }
                color="secondary"
                onChange={(e, expanded) => setDisplayHide((prevState) => ({ ...prevState, [status]: expanded }))}
              >
                <Grid container>
                  {status === 'eligible' ? (
                    <ParticipantsNamesGrid participants={participantsEligibility} />
                  ) : (
                    <EligibilityRulesTable participantsEligibility={participantsEligibility} />
                  )}
                </Grid>
              </TMXAccordion>
            </Grid>
          );
        }
        return null;
      }),
    [classes.accordionSummaryChildren, displayHide, participantsEligibilityDetails, t],
  );

  return (
    <Grid container item direction="column">
      <Grid item>
        <FormLabelTextLarge>{t('eventEligibilitySummary')}</FormLabelTextLarge>
      </Grid>
      {!participantsEventEligibility?.length ? (
        <Grid item>
          <EligibilityEmptyState>
            <BodyRegular>{t('emptyEligibilityRules')}</BodyRegular>
          </EligibilityEmptyState>
        </Grid>
      ) : (
        <Grid item>{eligibilitySections}</Grid>
      )}
    </Grid>
  );
};
export default EligibilityContainer;
